import React, { useState, useEffect, useCallback } from 'react';
import {
    Search,
    Edit3,
    Save,
    X,
    Check,
    AlertCircle,
    User,
    Calendar,
    Clock,
    Package,
    FileText,
    DollarSign,
    RefreshCw,
    ChevronDown,
    ChevronUp,
    Filter,
    Eye,
    Download,
    Upload,
    Trash2,
    AlertTriangle
} from 'lucide-react';
import { API_URL } from '../config/api';
import './StudyTracking.css';

const StudyTracking = ({ embedded = false }) => {
    const [studies, setStudies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [monthFilter, setMonthFilter] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedStudy, setSelectedStudy] = useState(null);
    const [editData, setEditData] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [lastUpdated, setLastUpdated] = useState(null);
    const [newConsentPdf, setNewConsentPdf] = useState(null);
    const [newResultPdf, setNewResultPdf] = useState(null);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [cancelling, setCancelling] = useState(false);

    const statusOptions = [
        { value: 'all', label: 'Todos los estados' },
        { value: 'solicitado', label: 'Solicitado' },
        { value: 'pendiente_muestra', label: 'Pendiente Muestra' },
        { value: 'muestra_tomada', label: 'Muestra Tomada' },
        { value: 'enviado', label: 'Enviado a Lab' },
        { value: 'en_laboratorio', label: 'En Laboratorio' },
        { value: 'finalizado', label: 'Finalizado' },
        { value: 'cancelado', label: 'Cancelado' }
    ];

    const fetchStudies = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (statusFilter !== 'all') params.append('status', statusFilter);
            if (searchTerm) params.append('search', searchTerm);

            const res = await fetch(`${API_URL}/studies/tracking?${params}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                const data = await res.json();
                setStudies(Array.isArray(data) ? data : (data.studies || data.data || []));
                setLastUpdated(new Date());
            } else {
                const errData = await res.json().catch(() => ({}));
                setError(errData.message || `Error ${res.status}: No se pudieron cargar los estudios`);
            }
        } catch (err) {
            console.error('Error fetching studies:', err);
            setError('Error de conexión al cargar estudios');
        } finally {
            setLoading(false);
        }
    }, [statusFilter, searchTerm]);

    // Fetch on filter or search change (with debounce)
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchStudies();
        }, 300);
        return () => clearTimeout(timer);
    }, [fetchStudies]);

    const openEditModal = (study) => {
        setSelectedStudy(study);
        const sanitizeDate = (d) => (d && d !== '0000-00-00' ? d.slice(0, 10) : '');
        const sanitizeDateTime = (d) => (d && d !== '0000-00-00 00:00:00' ? d.slice(0, 16) : '');

        setEditData({
            priority: study.priority || 'rutina',
            status: study.status || 'solicitado',
            special_notes: study.special_notes || '',
            sample_collected_at: sanitizeDateTime(study.sample_collected_at),
            sent_to_lab_at: sanitizeDateTime(study.sent_to_lab_at),
            completed_at: sanitizeDateTime(study.completed_at),
            pagado_proveedor: !!study.pagado_proveedor,
            reporte_entregado: !!study.reporte_entregado,
            muestra_enviada: !!study.muestra_enviada,
            sample_notes: study.sample_notes || '',
            interpretation: study.interpretation || '',
            shipment_notes: study.shipment_notes || ''
        });
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setSelectedStudy(null);
        setEditData({});
        setNewConsentPdf(null);
        setNewResultPdf(null);
    };

    const saveChanges = async () => {
        if (!selectedStudy) return;

        try {
            setSaving(true);
            setError('');
            const token = localStorage.getItem('token');

            // Sanitize data before sending
            const payload = { ...editData };
            if (payload.sample_collected_at === '') payload.sample_collected_at = null;
            if (payload.sent_to_lab_at === '') payload.sent_to_lab_at = null;
            if (payload.completed_at === '') payload.completed_at = null;

            // Ensure notes are included
            payload.sample_notes = editData.sample_notes;
            payload.interpretation = editData.interpretation;
            payload.shipment_notes = editData.shipment_notes;

            // First update the study data
            const res = await fetch(`${API_URL}/patient-studies/${selectedStudy.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            // If there's a new consent PDF, upload it
            if (res.ok && newConsentPdf) {
                const formData = new FormData();
                formData.append('consent_pdf', newConsentPdf);

                await fetch(`${API_URL}/patient-studies/${selectedStudy.id}/consent-pdf`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    body: formData
                });
            }

            // If there's a new result PDF, upload it
            if (res.ok && newResultPdf) {
                const resultFormData = new FormData();
                resultFormData.append('result_pdf', newResultPdf);

                await fetch(`${API_URL}/patient-studies/${selectedStudy.id}/result-pdf`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    body: resultFormData
                });
            }

            if (res.ok) {
                closeEditModal();
                fetchStudies();
            } else {
                const data = await res.json();
                setError(data.message || 'Error al guardar cambios');
            }
        } catch (err) {
            setError('Error de conexión');
        } finally {
            setSaving(false);
        }
    };

    const toggleVerification = async (study, field) => {
        try {
            const token = localStorage.getItem('token');
            const newValue = !study[field];

            // Optimistic update
            const updatedStudy = { ...study, [field]: newValue };
            setStudies(prevStudies => prevStudies.map(s => s.id === study.id ? updatedStudy : s));

            const res = await fetch(`${API_URL}/patient-studies/${study.id}/verification`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ [field]: newValue })
            });

            if (!res.ok) {
                // Revert on error
                fetchStudies();
                const data = await res.json();
                alert(data.message || 'Error al actualizar estado');
            }
        } catch (err) {
            console.error(err);
            fetchStudies(); // Revert
        }
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            solicitado: { label: 'Solicitado', class: 'status-solicitado' },
            pendiente_muestra: { label: 'Pendiente Muestra', class: 'status-pendiente' },
            muestra_tomada: { label: 'Muestra Tomada', class: 'status-tomada' },
            enviado: { label: 'Enviado a Lab', class: 'status-enviado' },
            en_laboratorio: { label: 'En Laboratorio', class: 'status-lab' },
            finalizado: { label: 'Finalizado', class: 'status-finalizado' },
            cancelado: { label: 'Cancelado', class: 'status-cancelado' }
        };
        const info = statusMap[status] || { label: status, class: '' };
        return <span className={`status-badge ${info.class}`}>{info.label}</span>;
    };

    // Get estatus based on study progress
    const getEstatus = (study) => {
        // Priority: Cancelado > Finalizado > Enviado > Muestra > Pendiente
        if (study.status === 'cancelado') {
            return { label: 'Cancelado', class: 'estatus-cancelado' };
        }
        if (study.pdf_path || study.status === 'finalizado') {
            return { label: 'Finalizado', class: 'estatus-finalizado' };
        }
        if (study.status === 'en_laboratorio') {
            return { label: 'En Laboratorio', class: 'estatus-en-lab' };
        }
        if (study.status === 'enviado') {
            return { label: 'Enviado a Lab', class: 'estatus-enviado' };
        }
        // "Muestra" when sample has been collected
        if (study.sample_collected_at || study.status === 'muestra_tomada') {
            return { label: 'Muestra Tomada', class: 'estatus-muestra' };
        }
        if (study.status === 'pendiente_muestra') {
            return { label: 'Pendiente Muestra', class: 'estatus-pendiente' };
        }
        return { label: 'Solicitado', class: 'estatus-solicitado' };
    };

    // Cancel study handler
    const cancelStudy = async () => {
        if (!selectedStudy) return;

        try {
            setCancelling(true);
            const token = localStorage.getItem('token');

            const res = await fetch(`${API_URL}/patient-studies/${selectedStudy.id}/cancel`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (res.ok) {
                setShowCancelConfirm(false);
                closeEditModal();
                fetchStudies();
            } else {
                const data = await res.json();
                setError(data.message || 'Error al cancelar el estudio');
                setShowCancelConfirm(false);
            }
        } catch (err) {
            setError('Error de conexión al cancelar');
            setShowCancelConfirm(false);
        } finally {
            setCancelling(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr || dateStr === '0000-00-00') return '-';
        return new Date(dateStr).toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Check if muestra was sent (based on sent_to_lab_at field from Envíos)
    const isMuestraEnviada = (study) => {
        return !!study.sent_to_lab_at || !!study.muestra_enviada;
    };

    // Helper to safely parse YYYY-MM-DD and prevent timezone shifts
    const parseLocalYMD = (dateStr) => {
        if (!dateStr || dateStr === '0000-00-00') return null;
        // Handle YYYY-MM-DD specific case to keep it local
        if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            const [year, month, day] = dateStr.split('-').map(Number);
            return new Date(year, month - 1, day);
        }
        // Fallback for datetimes
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? null : d;
    };

    const toYMD = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Calculate vigencia status based on sample date and catalog estimated time
    const getVigenciaStatus = (study) => {
        // If the study is already finished, it doesn't need a validity counter
        if (study.status === 'finalizado') {
            return { status: 'finalizado', label: 'Finalizado', class: 'vigencia-finalizado' };
        }
        if (study.status === 'cancelado') {
            return { status: 'cancelado', label: 'Cancelado', class: 'vigencia-neutral' };
        }

        let expectedDate = null;

        if (study.sample_collected_at && study.estimated_time) {
            const collectedDate = parseLocalYMD(study.sample_collected_at);
            if (collectedDate) {
                expectedDate = new Date(collectedDate);
                expectedDate.setDate(expectedDate.getDate() + parseInt(study.estimated_time));
                // Set to end of day to be fair
                expectedDate.setHours(23, 59, 59, 999);
            }
        } else if (study.expected_results_date) {
            // Fallback for legacy data
            expectedDate = parseLocalYMD(study.expected_results_date);
        }

        if (!expectedDate) return { status: 'sin_fecha', label: 'Sin fecha', class: 'vigencia-neutral' };

        const today = new Date();
        // Compare against today (start of day)
        const checkDate = new Date(today);
        checkDate.setHours(0, 0, 0, 0);

        const diffTime = expectedDate - checkDate;
        const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (daysUntil < 0) return { status: 'vencido', label: 'Vencido', class: 'vigencia-vencido' };
        if (daysUntil <= 2) return { status: 'proximo', label: 'Próximo', class: 'vigencia-proximo' };
        return { status: 'vigente', label: 'Vigente', class: 'vigencia-vigente' };
    };

    const getCalculatedExpectedDate = (study) => {
        if (study.sample_collected_at && study.estimated_time) {
            const collectedDate = parseLocalYMD(study.sample_collected_at);
            if (collectedDate) {
                const expected = new Date(collectedDate);
                expected.setDate(expected.getDate() + parseInt(study.estimated_time));
                return toYMD(expected);
            }
        }
        return study.expected_results_date;
    };

    const formatDateShort = (dateStr) => {
        const date = parseLocalYMD(dateStr);
        if (!date) return '-';

        return date.toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'short'
        });
    };

    const filteredStudies = studies.filter(study => {
        const patientName = `${study.patient_first_name || ''} ${study.patient_last_name || ''}`.toLowerCase();
        const studyName = (study.study_name || '').toLowerCase();
        const search = searchTerm.toLowerCase();
        
        // Month filter logic
        let matchesMonth = true;
        if (monthFilter && study.sample_collected_at) {
            // Ensure we have a string before using substring
            const dateStr = String(study.sample_collected_at);
            if (dateStr.length >= 7) {
                const studyMonth = dateStr.substring(0, 7); // YYYY-MM
                matchesMonth = studyMonth === monthFilter;
            } else {
                matchesMonth = false;
            }
        } else if (monthFilter && !study.sample_collected_at) {
            // If filter is active but study has no sample date, hide it
            matchesMonth = false;
        }

        return (patientName.includes(search) || studyName.includes(search)) && matchesMonth;
    });

    return (
        <div className={`study-tracking ${embedded ? 'embedded' : ''}`}>
            {!embedded && (
                <div className="tracking-header">
                    <h1>Seguimiento de Estudios</h1>
                    <p>Gestión y verificación del estado de todos los estudios</p>
                </div>
            )}

            {/* Filters */}
            <div className="tracking-filters">
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="Buscar por paciente o estudio..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="filter-group">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        {statusOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
                <div className="month-filter-wrapper">
                    {monthFilter && (
                        <button 
                            className="btn-clear-filter-outside" 
                            onClick={() => setMonthFilter('')}
                            title="Limpiar filtro de mes"
                        >
                            <X size={16} />
                        </button>
                    )}
                    <div className="filter-group">
                        <input
                            type="month"
                            value={monthFilter}
                            onChange={(e) => setMonthFilter(e.target.value)}
                            className="month-picker-input"
                        />
                    </div>
                </div>
                <button className="btn-refresh" onClick={fetchStudies}>
                    <RefreshCw size={18} />
                    Actualizar
                </button>
            </div>

            {error && (
                <div className="error-alert">
                    <AlertCircle size={18} />
                    {error}
                    <button onClick={() => setError('')}><X size={16} /></button>
                </div>
            )}

            {/* Stats Summary */}
            <div className="tracking-stats">
                <div className="stat-item">
                    <span className="stat-value">{studies.length}</span>
                    <span className="stat-label">Total Estudios</span>
                </div>
                <div className="stat-item">
                    <span className="stat-value">{studies.filter(s => s.status === 'finalizado').length}</span>
                    <span className="stat-label">Finalizados</span>
                </div>
                <div className="stat-item">
                    <span className="stat-value">{studies.filter(s => s.reporte_entregado).length}</span>
                    <span className="stat-label">Reportes Entregados</span>
                </div>
                {lastUpdated && (
                    <div className="last-updated">
                        <Clock size={14} />
                        Actualizado: {lastUpdated.toLocaleTimeString('es-MX')}
                    </div>
                )}
            </div>

            {/* Studies Table */}
            <div className="tracking-table-container">
                {loading ? (
                    <div className="loading-state">
                        <RefreshCw className="spin" size={32} />
                        <p>Cargando estudios...</p>
                    </div>
                ) : filteredStudies.length === 0 ? (
                    <div className="empty-state">
                        <FileText size={48} />
                        <p>No se encontraron estudios</p>
                    </div>
                ) : (
                    <table className="tracking-table">
                        <thead>
                            <tr>
                                <th>Paciente</th>
                                <th>Estudio</th>
                                <th>Estatus</th>
                                <th>
                                    <Calendar size={14} />
                                    F. Muestra
                                </th>
                                <th>
                                    <Package size={14} />
                                    Muestra Env.
                                </th>
                                <th>
                                    <DollarSign size={14} />
                                    Pagado Prov.
                                </th>
                                <th>
                                    <FileText size={14} />
                                    Reporte Ent.
                                </th>
                                <th>Notas</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredStudies.map(study => (
                                <tr key={study.id}>
                                    <td className="patient-cell">
                                        <User size={16} />
                                        <span>{study.patient_first_name} {study.patient_last_name}</span>
                                    </td>
                                    <td className="study-cell">
                                        <strong>{study.study_name}</strong>
                                        {study.priority === 'urgente' && (
                                            <span className="urgent-badge">URGENTE</span>
                                        )}
                                    </td>
                                    <td className="estatus-cell">
                                        {(() => {
                                            const estatus = getEstatus(study);
                                            return (
                                                <span className={`estatus-badge ${estatus.class}`}>
                                                    {estatus.label}
                                                </span>
                                            );
                                        })()}
                                    </td>
                                    <td className="date-cell">
                                        {formatDateShort(study.sample_collected_at)}
                                    </td>
                                    <td className="checkbox-cell">
                                        <div
                                            className={`status-indicator ${isMuestraEnviada(study) ? 'checked' : ''}`}
                                            title="Ver en modal para editar"
                                        >
                                            {isMuestraEnviada(study) ? <Check size={16} /> : <X size={16} />}
                                        </div>
                                    </td>
                                    <td className="checkbox-cell">
                                        <div
                                            className={`status-indicator ${study.pagado_proveedor ? 'checked' : ''}`}
                                            title="Ver en modal para editar"
                                        >
                                            {study.pagado_proveedor ? <Check size={16} /> : <X size={16} />}
                                        </div>
                                    </td>
                                    <td className="checkbox-cell">
                                        <div
                                            className={`status-indicator ${study.reporte_entregado ? 'checked' : ''}`}
                                            title="Ver en modal para editar"
                                        >
                                            {study.reporte_entregado ? <Check size={16} /> : <X size={16} />}
                                        </div>
                                    </td>
                                    <td className="notes-preview-cell">
                                        {(study.special_notes || study.sample_notes || study.interpretation || study.shipment_notes) ? (
                                            <div className="notes-indicator" title="Este estudio tiene notas">
                                                <AlertCircle size={16} style={{ color: '#00BBBF' }} />
                                            </div>
                                        ) : '-'}
                                    </td>
                                    <td className="actions-cell">
                                        {study.consent_pdf_path && (
                                            <a
                                                href={study.consent_pdf_path.startsWith('http') ? study.consent_pdf_path : `${API_URL}/${study.consent_pdf_path}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn-consent-pdf"
                                                title="Descargar Consentimiento"
                                            >
                                                <Download size={18} />
                                            </a>
                                        )}
                                        {study.pdf_path && (
                                            <a
                                                href={study.pdf_path.startsWith('http') ? study.pdf_path : `${API_URL}/${study.pdf_path}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn-pdf"
                                                title="Ver PDF de Resultado"
                                            >
                                                <FileText size={18} />
                                            </a>
                                        )}
                                        <button
                                            className="btn-edit"
                                            onClick={() => openEditModal(study)}
                                            title="Editar estudio"
                                        >
                                            <Edit3 size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Edit Modal */}
            {showEditModal && selectedStudy && (
                <div className="modal-overlay" onClick={closeEditModal}>
                    <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Editar Estudio</h2>
                            <button className="btn-close" onClick={closeEditModal}>
                                <X size={24} />
                            </button>
                        </div>

                        <div className="modal-body">
                            {/* Study Info - Read only */}
                            <div className="info-section">
                                <h3>Información del Estudio</h3>
                                <div className="info-row">
                                    <span className="label">Paciente:</span>
                                    <span className="value">{selectedStudy.patient_first_name} {selectedStudy.patient_last_name}</span>
                                </div>
                                {selectedStudy.patient_code && (
                                    <div className="info-row">
                                        <span className="label">Cód. Paciente:</span>
                                        <span className="value" style={{ fontWeight: '700', color: '#083677' }}>{selectedStudy.patient_code}</span>
                                    </div>
                                )}
                                <div className="info-row">
                                    <span className="label">Estudio:</span>
                                    <span className="value">{selectedStudy.study_name}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Código:</span>
                                    <span className="value">{selectedStudy.study_code || '-'}</span>
                                </div>
                                {selectedStudy.folio && (
                                    <div className="info-row">
                                        <span className="label">Folio:</span>
                                        <span className="value" style={{ fontWeight: '700', color: '#0369a1' }}>{selectedStudy.folio}</span>
                                    </div>
                                )}
                            </div>

                            {/* Editable Fields */}
                            <div className="edit-section">
                                <h3>Datos Editables</h3>
                                    <div className="form-grid">
                                    <div className="form-group">
                                        <label>Prioridad</label>
                                        <select
                                            value={editData.priority}
                                            onChange={(e) => setEditData({ ...editData, priority: e.target.value })}
                                        >
                                            <option value="rutina">Rutina</option>
                                            <option value="urgente">Urgente</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Estado</label>
                                        <select
                                            value={editData.status}
                                            onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                                        >
                                            {statusOptions.filter(o => o.value !== 'all').map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    
                                    <div className="form-row-dates full-width">
                                        <div className="form-group">
                                            <label>Muestra</label>
                                            <input
                                                type="datetime-local"
                                                value={editData.sample_collected_at}
                                                onChange={(e) => setEditData({ ...editData, sample_collected_at: e.target.value })}
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label>Laboratorio</label>
                                            <input
                                                type="datetime-local"
                                                value={editData.sent_to_lab_at}
                                                onChange={(e) => setEditData({ ...editData, sent_to_lab_at: e.target.value })}
                                            />
                                        </div>
                                        
                                        <div className="form-group">
                                            <label>Finalización</label>
                                            <input
                                                type="datetime-local"
                                                value={editData.completed_at}
                                                onChange={(e) => setEditData({ ...editData, completed_at: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group full-width">
                                        <label>Consentimiento PDF</label>
                                        <div className="consent-pdf-section">
                                            {selectedStudy.consent_pdf_path && !newConsentPdf && (
                                                <a
                                                    href={selectedStudy.consent_pdf_path.startsWith('http') ? selectedStudy.consent_pdf_path : `${API_URL}/${selectedStudy.consent_pdf_path}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="pdf-link"
                                                >
                                                    <FileText size={16} />
                                                    Ver archivo actual
                                                </a>
                                            )}
                                            <input
                                                type="file"
                                                id="consent-pdf-edit"
                                                accept=".pdf"
                                                onChange={(e) => setNewConsentPdf(e.target.files[0])}
                                                style={{ display: 'none' }}
                                            />
                                            <label htmlFor="consent-pdf-edit" className="btn-upload-consent">
                                                <Upload size={16} />
                                                {newConsentPdf ? newConsentPdf.name : (selectedStudy.consent_pdf_path ? 'Cambiar archivo' : 'Cargar archivo')}
                                            </label>
                                            {newConsentPdf && (
                                                <button
                                                    type="button"
                                                    className="btn-remove-file"
                                                    onClick={() => setNewConsentPdf(null)}
                                                >
                                                    <X size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-group full-width">
                                        <label>Resultado PDF</label>
                                        <div className="consent-pdf-section">
                                            {selectedStudy.pdf_path && !newResultPdf && (
                                                <a
                                                    href={selectedStudy.pdf_path.startsWith('http') ? selectedStudy.pdf_path : `${API_URL}/${selectedStudy.pdf_path}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="pdf-link"
                                                >
                                                    <FileText size={16} />
                                                    Ver archivo actual
                                                </a>
                                            )}
                                            <input
                                                type="file"
                                                id="result-pdf-edit"
                                                accept=".pdf"
                                                onChange={(e) => setNewResultPdf(e.target.files[0])}
                                                style={{ display: 'none' }}
                                            />
                                            <label htmlFor="result-pdf-edit" className="btn-upload-consent" style={{ backgroundColor: '#00BBBF', borderColor: '#00BBBF', color: 'white' }}>
                                                <Upload size={16} />
                                                {newResultPdf ? newResultPdf.name : (selectedStudy.pdf_path ? 'Cambiar archivo' : 'Cargar archivo')}
                                            </label>
                                            {newResultPdf && (
                                                <button
                                                    type="button"
                                                    className="btn-remove-file"
                                                    onClick={() => setNewResultPdf(null)}
                                                >
                                                    <X size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    </div>
                                </div>

                            {/* Verification Checkboxes */}
                            <div className="verification-section">
                                <h3>Verificación</h3>
                                <div className="checkbox-list">
                                    <label className="checkbox-item readonly">
                                        <input
                                            type="checkbox"
                                            checked={!!editData.sent_to_lab_at || editData.muestra_enviada}
                                            disabled
                                        />
                                        <span>Muestra Enviada</span>
                                        <small>(Se marca automáticamente al enviar en la pestaña Envíos)</small>
                                    </label>
                                    <label className="checkbox-item">
                                        <input
                                            type="checkbox"
                                            checked={editData.pagado_proveedor}
                                            onChange={(e) => setEditData({ ...editData, pagado_proveedor: e.target.checked })}
                                        />
                                        <span>Pagado al Proveedor</span>
                                    </label>
                                    <label className="checkbox-item">
                                        <input
                                            type="checkbox"
                                            checked={editData.reporte_entregado}
                                            onChange={(e) => setEditData({ ...editData, reporte_entregado: e.target.checked })}
                                        />
                                        <span>Reporte Entregado</span>
                                    </label>
                                </div>
                            </div>

                            {/* Consolidated Notes Section - Bottom */}
                            <div className="consolidated-notes-section">
                                <h3>Observaciones del Proceso</h3>
                                <div className="notes-grid">
                                    <div className="form-group">
                                        <label>Notas de Registro</label>
                                        <textarea
                                            value={editData.special_notes}
                                            onChange={(e) => setEditData({ ...editData, special_notes: e.target.value })}
                                            rows={2}
                                            placeholder="Notas capturadas en recepción..."
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Notas de Enfermería (Muestra)</label>
                                        <textarea
                                            value={editData.sample_notes}
                                            onChange={(e) => setEditData({ ...editData, sample_notes: e.target.value })}
                                            rows={2}
                                            placeholder="Observaciones de la toma de muestra..."
                                            disabled={!selectedStudy.sample_collected_at}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Notas de Envíos</label>
                                        <textarea
                                            value={editData.shipment_notes}
                                            onChange={(e) => setEditData({ ...editData, shipment_notes: e.target.value })}
                                            rows={2}
                                            placeholder="Notas del envío a laboratorio..."
                                            disabled={!isMuestraEnviada(selectedStudy)}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Interpretación (Laboratorio)</label>
                                        <textarea
                                            value={editData.interpretation}
                                            onChange={(e) => setEditData({ ...editData, interpretation: e.target.value })}
                                            rows={2}
                                            placeholder="Interpretación de resultados..."
                                            disabled={!selectedStudy.pdf_path && selectedStudy.status !== 'finalizado'}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            {/* Cancel Study Button - Only show if not already cancelled */}
                            {selectedStudy.status !== 'cancelado' && (
                                <button
                                    className="btn-cancel-study"
                                    onClick={() => setShowCancelConfirm(true)}
                                    title="Cancelar este estudio"
                                >
                                    <Trash2 size={18} />
                                    Cancelar Estudio
                                </button>
                            )}
                            <div className="modal-footer-right">
                                <button className="btn-cancel" onClick={closeEditModal}>
                                    Cerrar
                                </button>
                                <button
                                    className="btn-save"
                                    onClick={saveChanges}
                                    disabled={saving}
                                >
                                    <Save size={18} />
                                    {saving ? 'Guardando...' : 'Guardar Cambios'}
                                </button>
                            </div>
                        </div>

                        {/* Cancel Confirmation Modal */}
                        {showCancelConfirm && (
                            <div className="confirm-overlay">
                                <div className="confirm-modal">
                                    <div className="confirm-icon">
                                        <AlertTriangle size={48} />
                                    </div>
                                    <h3>¿Está seguro que desea cancelar este estudio?</h3>
                                    <p>Esta acción no se puede deshacer. El estudio ya no aparecerá en las secciones de Muestras, Envíos y Laboratorio.</p>
                                    <div className="confirm-buttons">
                                        <button
                                            className="btn-confirm-no"
                                            onClick={() => setShowCancelConfirm(false)}
                                            disabled={cancelling}
                                        >
                                            No
                                        </button>
                                        <button
                                            className="btn-confirm-yes"
                                            onClick={cancelStudy}
                                            disabled={cancelling}
                                        >
                                            {cancelling ? 'Cancelando...' : 'Sí, Cancelar'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default StudyTracking;
